import type { Node as ProseMirrorNode } from '@tiptap/pm/model';
import { ReactRenderer } from '@tiptap/react';
import type { SuggestionKeyDownProps, SuggestionOptions, SuggestionProps } from '@tiptap/suggestion';

import type { MentionType } from '../../../utils/mentions/mentionType';

import type { MentionSuggestion, MentionSuggestionsSource } from '../../CommunityPostComposer.type';
import {
  MentionSuggestionList,
  type MentionSuggestionListHandle,
  type MentionSuggestionListProps,
} from './MentionSuggestionList';

export const MAX_MENTIONS = 40;

export const isMentionLimitReached = (doc: ProseMirrorNode, mentionId: string): boolean => {
  const mentionedIds = new Set<string>();
  doc.descendants((node) => {
    if (node.type.name === 'mention' && typeof node.attrs.id === 'string') mentionedIds.add(node.attrs.id);
  });
  return !mentionedIds.has(mentionId) && mentionedIds.size >= MAX_MENTIONS;
};

type SearchMembers = MentionSuggestionsSource['searchMembers'];

// Same wait as the chat member search in Orchid (useChatUsersQuery).
export const MENTION_SEARCH_DEBOUNCE_MS = 250;

// Only the last query within the debounce hits the host, and only the latest search delivers its results.
export const createMemberSearch = () => {
  let timer: ReturnType<typeof setTimeout> | undefined;
  let latestRequest = 0;

  const cancel = () => {
    clearTimeout(timer);
    latestRequest += 1;
  };

  const schedule = (searchMembers: SearchMembers, query: string, onResults: (items: MentionSuggestion[]) => void) => {
    cancel();
    const request = latestRequest;
    timer = setTimeout(async () => {
      const items = await searchMembers(query).catch(() => []);
      if (request === latestRequest) onResults(items);
    }, MENTION_SEARCH_DEBOUNCE_MS);
  };

  return { schedule, cancel };
};

export type MentionAttributes = { id: string; label: string; type: MentionType };

type MentionSuggestionProps = SuggestionProps<MentionSuggestion, MentionAttributes>;

export const createMentionSuggestion = (
  getSuggestions: () => MentionSuggestionsSource | undefined,
  listLabel: string,
): Omit<SuggestionOptions<MentionSuggestion, MentionAttributes>, 'editor'> => ({
  allowSpaces: false,

  allow: ({ state, range }) => {
    if (!getSuggestions()) return false;
    const $from = state.doc.resolve(range.from);
    return Boolean($from.parent.type.contentMatch.matchType(state.schema.nodes.mention));
  },

  // Suggestion awaits items on every update, and async items would deliver older props after newer ones.
  // The render lifecycle below runs the search instead, so the list always uses the latest range.
  items: () => [],

  render: () => {
    const search = createMemberSearch();
    let component: ReactRenderer<MentionSuggestionListHandle, MentionSuggestionListProps> | null = null;
    let latestProps: MentionSuggestionProps | null = null;
    let items: MentionSuggestion[] = [];
    let isOpen = false;

    const listProps = (props: MentionSuggestionProps): MentionSuggestionListProps => ({
      editor: props.editor,
      label: listLabel,
      items,
      clientRect: props.clientRect ?? null,
      onSelect: ({ id, name: label, type }) => {
        if (isMentionLimitReached(props.editor.state.doc, id)) {
          getSuggestions()?.onLimitReached();
          return;
        }
        props.command({ id, label, type });
      },
    });

    const renderList = () => {
      if (!latestProps) return;
      if (component) component.updateProps(listProps(latestProps));
      else
        component = new ReactRenderer(MentionSuggestionList, {
          props: listProps(latestProps),
          editor: latestProps.editor,
        });
    };

    const searchLatestQuery = () => {
      const suggestions = getSuggestions();
      if (!suggestions || !latestProps) return;
      search.schedule(suggestions.searchMembers, latestProps.query, (results) => {
        if (!isOpen) return;
        items = results;
        renderList();
      });
    };

    const close = () => {
      isOpen = false;
      search.cancel();
      component?.destroy();
      component = null;
    };

    return {
      onBeforeStart: (props) => {
        latestProps = props;
      },

      onBeforeUpdate: (props) => {
        latestProps = props;
      },

      // A caret that jumps between two queries calls onExit and then onStart in the same update.
      onStart: () => {
        isOpen = true;
        items = [];
        renderList();
        searchLatestQuery();
      },

      onUpdate: () => {
        if (!isOpen) return;
        renderList();
        searchLatestQuery();
      },

      onKeyDown: (props: SuggestionKeyDownProps) => {
        if (props.event.key === 'Escape') {
          // Only a list on screen keeps Escape, so a host modal still closes while the caret is in a query.
          const isListVisible = isOpen && items.length > 0;
          close();
          return isListVisible;
        }

        return component?.ref?.onKeyDown(props.event) ?? false;
      },

      onExit: close,
    };
  },
});
